/**
 * SupperTeacher submission page — full NVTT-parity entry workflow + inline
 * history.
 *
 * Layout:
 *   1. Page header (title + greeting)
 *   2. Meta card (hạng + ghi chú; loại derived from hạng)
 *   3. Scanner cards (simpleMode → "Thêm hồ sơ mới" with staging dialog
 *      that has 4 type pills CCCD/GPLX/Ảnh thẻ/Khác for AI accuracy)
 *   4. Inline "Chờ nộp hồ sơ" panel — background batch queue with the
 *      AI processing the staged docs; click a "done" row to open the
 *      review modal
 *   5. Inline "Lịch sử đã nộp" panel — past submissions + records the
 *      center entered on the supper teacher's behalf; pending rows
 *      can be edited via the same review modal (PUT call). Non-pending
 *      rows open read-only.
 *
 * The scanner form runs with `teacherFieldHidden` because the teacher is
 * implicit — the BE fills `submittedBy = req.user.id` from the JWT.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../features/auth/hooks/useAuth';
import {
  teacherSubmitApi,
  teacherUpdateSubmissionApi,
  getPendingRecordDetailApi,
  type PendingRecordDto,
  type TtSubmission,
  type SubmissionPayload,
  type TeacherHistoryDirectRecord,
} from '../../features/trungTam/services/trungTamApi';
import type { BatchItem, CccdScanResult, ExtraImage, ScanMethod, TtCourse } from '../../features/trungTam/types';
import { CENTER_ACTIVE_HANGS } from '../../features/trungTam/types';
import TtScannerForm from '../../features/trungTam/components/TtScannerForm';
import RecordDetailModal from '../../features/trungTam/components/RecordDetailModal';
import { useBatchQueue } from '../../features/trungTam/hooks/useBatchQueue';
import BreadcrumbBar from '../DashBoardPage/BreadcrumbBar';
import SubmissionHistoryList from './SubmissionHistoryList';
import './TeacherSubmitPage.scss';
import '../../features/trungTam/trungTam.scss';

const HANG_OPTIONS = CENTER_ACTIVE_HANGS.map(h => ({
  value: h.value,
  label: h.desc,
}));

const isNangHangFor = (hangValue: string): boolean =>
  CENTER_ACTIVE_HANGS.find(h => h.value === hangValue)?.isNangHang ?? false;

const asDataUrl = (b64: string | null | undefined): string | null => {
  if (!b64) return null;
  return b64.startsWith('data:') ? b64 : `data:image/jpeg;base64,${b64}`;
};

/** Convert a PendingRecordDto into a CccdScanResult so the batch review
 *  UI can render it. Mirrors the helper in PendingRecordsTab. */
const pendingToScanResult = (p: PendingRecordDto): CccdScanResult => {
  let extras: ExtraImage[] = [];
  if (p.extraImagesJson) {
    try {
      const parsed = JSON.parse(p.extraImagesJson);
      if (Array.isArray(parsed)) extras = parsed;
    } catch { /* ignore malformed JSON */ }
  }
  return {
    soCCCD: p.soCCCD || '',
    soCMT: p.soCMT || '',
    hoTen: p.hoTen || '',
    ngaySinh: p.ngaySinh || '',
    gioiTinh: p.gioiTinh || '',
    diaChi: p.diaChi || '',
    diaChiMoi: p.diaChiCap2 || '',
    ngayCap: p.ngayCap || '',
    avatarBase64: asDataUrl(p.avatarBase64),
    sourceImageBase64: asDataUrl(p.sourceImageBase64),
    detectedMethod: p.scanMethod || null,
    cccdSource: p.cccdSource ?? null,
    gplxSource: p.gplxSource ?? null,
    extraImages: extras,
    soGPLX: p.soGPLX || '',
    hangGPLX: p.hangGPLX || '',
    hangGPLXMoi: p.hangGPLXMoi || '',
    ngayCapGPLX: p.ngayCapGPLX || '',
    noiCapGPLX: '',
    donViCapGPLX: '',
    ngayTrungTuyenGPLX: p.ngayTrungTuyenGPLX || '',
    ngayHetHanGPLX: p.ngayHetHanGPLX || '',
    maDvhc: p.maDvhc || '',
    dvhcTenDayDu: p.dvhcTenDayDu || undefined,
    portraitBase64: asDataUrl(p.portraitBase64),
    portraitOriginalBase64: asDataUrl(p.portraitOriginalBase64),
    portraitAiVariantsJson: p.portraitAiVariantsJson || null,
    faceMatchScore: p.faceMatchScore ?? null,
    teacherId: p.teacherId ?? null,
    teacherFullName: p.teacher?.username ?? null,
  };
};

const submissionToBatchItem = (sub: TtSubmission): BatchItem => {
  // Parse extras saved as JSON on the queue row. Same shape the pending
  // queue + student record use elsewhere — review gallery renders it
  // straight as the "Giấy tờ đính kèm" thumbs.
  let extras: ExtraImage[] = [];
  if (sub.extraImagesJson) {
    try {
      const parsed = JSON.parse(sub.extraImagesJson);
      if (Array.isArray(parsed)) extras = parsed;
    } catch { /* malformed JSON — render with empty extras */ }
  }
  const result: CccdScanResult = {
    soCCCD: sub.soCCCD || '',
    soCMT: sub.soCMT || '',
    hoTen: sub.hoTen || '',
    ngaySinh: sub.ngaySinh || '',
    gioiTinh: sub.gioiTinh || '',
    diaChi: sub.diaChi || '',
    diaChiMoi: sub.dvhcTenDayDu || '',
    ngayCap: sub.ngayCap || '',
    avatarBase64: asDataUrl(sub.avatarBase64),
    sourceImageBase64: asDataUrl(sub.sourceImageBase64),
    detectedMethod: sub.scanMethod || null,
    cccdSource: null,
    gplxSource: null,
    extraImages: extras,
    // GPLX structured fields aren't stored on the queue row (only the
    // images live in extraImagesJson). Leave empty — the review UI
    // shows GPLX as image cards inside "Giấy tờ đính kèm".
    soGPLX: '',
    hangGPLX: '',
    hangGPLXMoi: '',
    ngayCapGPLX: '',
    noiCapGPLX: '',
    donViCapGPLX: '',
    ngayTrungTuyenGPLX: '',
    ngayHetHanGPLX: '',
    maDvhc: sub.maDvhc || '',
    dvhcTenDayDu: sub.dvhcTenDayDu || undefined,
    portraitBase64: asDataUrl(sub.portraitBase64),
    portraitOriginalBase64: null,
    portraitAiVariantsJson: null,
    faceMatchScore: sub.faceMatchScore ?? null,
    teacherId: null,
    teacherFullName: null,
  };
  return {
    fileName: result.hoTen || result.soCCCD || `Submission-${sub.id}`,
    preview: result.sourceImageBase64 || '',
    status: 'done',
    result,
    pendingRecordId: sub.id, // reused slot — modal threads it back to onSubmit
  };
};

const directRecordToBatchItem = (rec: TeacherHistoryDirectRecord): BatchItem => {
  const result: CccdScanResult = {
    soCCCD: rec.soCCCD || '',
    soCMT: '',
    hoTen: rec.hoTen || '',
    ngaySinh: rec.ngaySinh || '',
    gioiTinh: '',
    diaChi: '',
    diaChiMoi: '',
    ngayCap: '',
    avatarBase64: null,
    sourceImageBase64: null,
    detectedMethod: null,
    cccdSource: null,
    gplxSource: null,
    extraImages: [],
    soGPLX: '',
    hangGPLX: '',
    hangGPLXMoi: '',
    ngayCapGPLX: '',
    noiCapGPLX: '',
    donViCapGPLX: '',
    ngayTrungTuyenGPLX: '',
    ngayHetHanGPLX: '',
    maDvhc: '',
    portraitBase64: null,
    portraitOriginalBase64: null,
    portraitAiVariantsJson: null,
    faceMatchScore: null,
    teacherId: null,
    teacherFullName: null,
  };
  return {
    fileName: result.hoTen || result.soCCCD || `Record-${rec.id}`,
    preview: '',
    status: 'done',
    result,
  };
};

/** Three modal modes:
 *   - submit-from-queue: "Sẵn sàng nộp" row → review → POST teacherSubmit
 *   - edit-submission:   pending submission in lịch sử → review → PUT
 *   - view:              non-pending submission OR direct record → readOnly
 */
type ModalState =
  | { kind: 'closed' }
  | { kind: 'submit-from-queue'; items: BatchItem[] }
  | { kind: 'edit-submission'; submissionId: number; items: BatchItem[] }
  | { kind: 'view'; items: BatchItem[]; courseInfo: TtCourse | null };

const SubmitRecordPage: React.FC = () => {
  const { displayName } = useAuth();
  const [hang, setHang] = useState<string>('B');
  const [note, setNote] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [modal, setModal] = useState<ModalState>({ kind: 'closed' });
  const [openingDetail, setOpeningDetail] = useState(false);

  const isNangHang = isNangHangFor(hang);

  const composedNote = (raw: string): string => {
    const tag = isNangHang ? '[Nâng hạng]' : '[Học mới]';
    const free = raw.trim();
    return free ? `${tag} ${free}` : tag;
  };

  // Same hook NVTT uses — silent multi-record AI processing in the
  // background. Teacher pending rows have ttCourseId=null (teachers
  // don't pick a course); BE listPending filters by createdBy so
  // other operators don't see them.
  const batchQueue = useBatchQueue();
  const queueRecords = batchQueue.queueRecords;

  const { queuePendingCount, queueDoneCount } = useMemo(() => ({
    queuePendingCount: queueRecords.filter(r => r.status === 'pending' || r.status === 'detecting').length,
    queueDoneCount:    queueRecords.filter(r => r.status === 'done').length,
  }), [queueRecords]);

  /** Submit handler for both:
   *  - Single-shot review modal opened via queue "done" row
   *  - Pending re-submit via "Sẵn sàng nộp" cards
   *  Calls teacherSubmitApi → on success, deletes the local pending row.
   */
  const handleSubmitFromQueue = useCallback(async (
    data: CccdScanResult & { scanMethod: ScanMethod; pendingRecordId?: number },
  ): Promise<boolean> => {
    if (!hang) {
      toast.warn('Hãy chọn hạng trước khi nộp');
      return false;
    }
    if (!data.hoTen) {
      toast.warn('Thiếu Họ tên');
      return false;
    }
    try {
      const resp = await teacherSubmitApi({
        scanMethod: data.scanMethod,
        soCCCD: data.soCCCD || null,
        soCMT: data.soCMT || null,
        hoTen: data.hoTen,
        ngaySinh: data.ngaySinh || null,
        gioiTinh: data.gioiTinh || null,
        diaChi: data.diaChi || null,
        ngayCap: data.ngayCap || null,
        avatarBase64: data.avatarBase64 || null,
        portraitBase64: data.portraitBase64 || null,
        sourceImageBase64: data.sourceImageBase64 || null,
        faceMatchScore: data.faceMatchScore ?? null,
        maDvhc: data.maDvhc || null,
        dvhcTenDayDu: data.dvhcTenDayDu || null,
        extraImages: data.extraImages?.length ? data.extraImages : undefined,
        hang,
        note: composedNote(note),
      });
      if (resp.EC === 0) {
        const sub = resp.DT;
        if (sub && sub.seqNo > 0) {
          toast.success(`Đã nộp ${data.hoTen} — STT #${sub.seqNo} hạng ${sub.hang}`);
        } else {
          toast.success(`Đã nộp ${data.hoTen}`);
        }
        if (data.pendingRecordId) batchQueue.deleteLocal(data.pendingRecordId);
        // Drop the just-saved item from modal state (auto-close when empty).
        setModal(prev => {
          if (prev.kind !== 'submit-from-queue') return prev;
          const remaining = prev.items.filter(p => p.pendingRecordId !== data.pendingRecordId);
          return remaining.length === 0
            ? { kind: 'closed' }
            : { kind: 'submit-from-queue', items: remaining };
        });
        setReloadKey(k => k + 1);
        return true;
      }
      toast.warn(resp.EM || 'Không nộp được hồ sơ');
      return false;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { EM?: string } } };
      toast.error(e?.response?.data?.EM || 'Lỗi khi nộp hồ sơ');
      return false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hang, note, isNangHang, batchQueue]);

  /** Edit an existing pending submission via PUT. Re-allocates seqNo
   *  per Phương án X (record moves to end of queue). */
  const handleEditSubmission = useCallback(async (
    submissionId: number,
    data: CccdScanResult & { scanMethod: ScanMethod; pendingRecordId?: number },
  ): Promise<boolean> => {
    try {
      const patch: Partial<SubmissionPayload> = {
        scanMethod: data.scanMethod,
        soCCCD: data.soCCCD || null,
        soCMT: data.soCMT || null,
        hoTen: data.hoTen,
        ngaySinh: data.ngaySinh || null,
        gioiTinh: data.gioiTinh || null,
        diaChi: data.diaChi || null,
        ngayCap: data.ngayCap || null,
        avatarBase64: data.avatarBase64 || null,
        portraitBase64: data.portraitBase64 || null,
        sourceImageBase64: data.sourceImageBase64 || null,
        faceMatchScore: data.faceMatchScore ?? null,
        maDvhc: data.maDvhc || null,
        dvhcTenDayDu: data.dvhcTenDayDu || null,
        extraImages: data.extraImages?.length ? data.extraImages : undefined,
        hang,
        note: composedNote(note),
      };
      const resp = await teacherUpdateSubmissionApi(submissionId, patch);
      if (resp.EC === 0) {
        toast.success('Đã cập nhật hồ sơ');
        setModal({ kind: 'closed' });
        setReloadKey(k => k + 1);
        return true;
      }
      toast.warn(resp.EM || 'Không cập nhật được');
      return false;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { EM?: string } } };
      toast.error(e?.response?.data?.EM || 'Lỗi khi cập nhật');
      return false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hang, note, isNangHang]);

  // Open a "done" pending row in the review modal.
  const openReviewForQueue = useCallback(async (recordId: number) => {
    setOpeningDetail(true);
    try {
      const resp = await getPendingRecordDetailApi(recordId);
      if (resp.EC === 0 && resp.DT) {
        const p = resp.DT;
        const result = pendingToScanResult(p);
        const item: BatchItem = {
          fileName: result.hoTen || result.soCCCD || `Record-${p.id}`,
          preview: result.sourceImageBase64 || '',
          status: 'done',
          result,
          pendingRecordId: p.id,
        };
        setModal({ kind: 'submit-from-queue', items: [item] });
      } else {
        toast.error(resp.EM || 'Không tải được chi tiết hồ sơ');
      }
    } catch {
      toast.error('Lỗi khi tải chi tiết hồ sơ');
    } finally {
      setOpeningDetail(false);
    }
  }, []);

  // Auto-open the review modal when a freshly-detected pending row
  // becomes "done" during this page session. Items already done when the
  // queue loads on mount (leftovers from previous sessions) are marked as
  // seen immediately so they don't trigger the modal automatically —
  // the operator can click them manually from the history list.
  const seenDoneIdsRef = useRef<Set<number>>(new Set());
  const queueInitializedRef = useRef(false);
  useEffect(() => {
    if (!queueInitializedRef.current) {
      // Mark the initial server-loaded items: only suppress items already
      // "done" on mount (leftovers from a previous session). Items that are
      // still "pending" or "detecting" may transition to "done" in this
      // session and should auto-open the modal normally.
      queueInitializedRef.current = true;
      for (const r of queueRecords) {
        if (r.status === 'done') seenDoneIdsRef.current.add(r.id);
      }
      return;
    }
    if (modal.kind !== 'closed') return; // don't interrupt an open modal
    const newlyDone = queueRecords.find(
      (r) => r.status === 'done' && !seenDoneIdsRef.current.has(r.id),
    );
    if (newlyDone) {
      seenDoneIdsRef.current.add(newlyDone.id);
      void openReviewForQueue(newlyDone.id);
    }
    // Prune ids that no longer exist (deleted/saved) so the set doesn't
    // grow unbounded across long sessions.
    const liveIds = new Set(queueRecords.map((r) => r.id));
    for (const id of seenDoneIdsRef.current) {
      if (!liveIds.has(id)) seenDoneIdsRef.current.delete(id);
    }
  }, [queueRecords, modal.kind, openReviewForQueue]);

  return (
    <div className="tt-page">
      <BreadcrumbBar
        crumbs={[
          { label: 'Tổng quan', to: '/dashboard/home' },
          { label: 'Nộp hồ sơ học viên' },
        ]}
      />

      <div className="tt-page__header">
        <h1 className="tt-page__title">
          <i className="material-icons">assignment_ind</i>
          Nộp hồ sơ học viên
        </h1>
        <span className="ts-greet">
          <i className="material-icons">person</i>
          {displayName || 'Supper teacher'}
        </span>
      </div>

      {/* Meta — hạng + ghi chú; loại derived from hạng */}
      <div className="ts-meta-card">
        <label className="ts-meta-field">
          <span className="ts-meta-field__label">
            <i className="material-icons">workspace_premium</i>
            Hạng hồ sơ <b className="ts-required">*</b>
          </span>
          <div className="ts-hang-row">
            <select
              className="ts-meta-field__input ts-hang-row__select"
              value={hang}
              onChange={(e) => setHang(e.target.value)}
            >
              {HANG_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <span
              className={`ts-hang-hint${isNangHang ? ' ts-hang-hint--nang' : ' ts-hang-hint--moi'}`}
              title={isNangHang
                ? 'Hạng nâng hạng — bắt buộc nhập GPLX cũ'
                : 'Học mới — GPLX không bắt buộc, có thể nhập nếu có'}
            >
              <i className="material-icons">{isNangHang ? 'upgrade' : 'school'}</i>
              {isNangHang ? 'Nâng hạng — cần GPLX cũ' : 'Học mới — GPLX không bắt buộc'}
            </span>
          </div>
        </label>

        <label className="ts-meta-field ts-meta-field--full">
          <span className="ts-meta-field__label">
            <i className="material-icons">edit_note</i>
            Ghi chú (tùy chọn)
          </span>
          <input
            type="text"
            className="ts-meta-field__input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="VD: học viên ưu tiên, có ghi chú đặc biệt..."
            maxLength={500}
          />
        </label>
      </div>

      {/* Scanner — simpleMode: same staging dialog NVTT uses, with the
          CCCD/GPLX/Ảnh thẻ/Khác type pills feeding the AI prompt for
          accurate detection. teacherFieldHidden because the supper
          teacher IS the teacher — server fills submittedBy from JWT. */}
      <div className="ts-scanner-wrap">
        <TtScannerForm
          simpleMode
          onSubmit={handleSubmitFromQueue}
          onAddToQueue={(files) => batchQueue.addRecord(files, null)}
          onAddToQueueWithHints={(items) => batchQueue.addRecord(items, null)}
          submitLabel="Nộp hồ sơ"
          submitIcon="send"
          isNangHangCourse={isNangHang}
          teacherFieldHidden
          queueCount={queueRecords.length}
          queuePendingCount={queuePendingCount}
          queueDoneCount={queueDoneCount}
        />
      </div>

      {/* Lịch sử đã nộp — bên dưới scanner. The old "Chờ nộp hồ sơ"
          panel was removed: when a queue item finishes background AI
          processing, the review modal auto-opens (see the useEffect
          watching queueRecords above), so the operator goes straight
          from upload → review → submit without an intermediate list.
          Errored items still surface via toast inside the batch hook. */}
      <SubmissionHistoryList
        reloadKey={reloadKey}
        onEditSubmission={(sub) => {
          setHang(sub.hang || 'B');
          const tagged = sub.note || '';
          const stripped = tagged.replace(/^\[(Nâng hạng|Học mới)\]\s*/, '');
          setNote(stripped);
          setModal({
            kind: 'edit-submission',
            submissionId: sub.id,
            items: [submissionToBatchItem(sub)],
          });
        }}
        onViewSubmission={(sub) => {
          setModal({
            kind: 'view',
            items: [submissionToBatchItem(sub)],
            courseInfo: sub.linkedCourse
              ? ({ id: sub.linkedCourse.id, name: sub.linkedCourse.name, hang: sub.linkedCourse.hang } as TtCourse)
              : null,
          });
        }}
        onViewDirectRecord={(rec) => {
          setModal({
            kind: 'view',
            items: [directRecordToBatchItem(rec)],
            courseInfo: rec.course
              ? ({ id: rec.course.id, name: rec.course.name, hang: rec.course.hang } as TtCourse)
              : null,
          });
        }}
      />

      {/* Single review modal serves all 3 kinds */}
      <RecordDetailModal
        open={modal.kind !== 'closed'}
        items={modal.kind === 'closed' ? [] : modal.items}
        courses={modal.kind === 'view' && modal.courseInfo ? [modal.courseInfo] : []}
        selectedCourseId={modal.kind === 'view' && modal.courseInfo ? modal.courseInfo.id : null}
        onClose={() => setModal({ kind: 'closed' })}
        onSubmit={async (data) => {
          if (modal.kind === 'edit-submission') {
            return handleEditSubmission(modal.submissionId, data);
          }
          if (modal.kind === 'submit-from-queue') {
            return handleSubmitFromQueue(data);
          }
          return false;
        }}
        readOnly={modal.kind === 'view'}
        teacherFieldHidden
        submitLabel={modal.kind === 'edit-submission' ? 'Cập nhật' : modal.kind === 'submit-from-queue' ? 'Nộp hồ sơ' : undefined}
        submitIcon={modal.kind === 'edit-submission' ? 'save' : modal.kind === 'submit-from-queue' ? 'send' : undefined}
      />

      {openingDetail && (
        <div className="ts-pending-loading-overlay" aria-live="polite">
          <span className="tt-spinner" />
          <span>Đang tải hồ sơ…</span>
        </div>
      )}
    </div>
  );
};

export default SubmitRecordPage;
